import { Alert, Box, Checkbox, CircularProgress, Dialog, FormControlLabel } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { ReceiptPrintDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import SortingHandler from 'src/common/tableRelated/SortingHandler'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import MentorPastEventsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getUserRoles } from 'src/mentor/layout/utils'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import {
  canViewSupportBookings,
  canViewSupportVcAuditBookings,
  getAllowedSupportBookedEventTypeOptions,
  SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS,
  canManageSupportBookedEventInvoices,
  canManageSupportRecordingPreferences,
} from 'src/supportPortal/access/accessMatrix'
import { BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { getSPDataWithoutParams, postSPDataWithoutParams } from 'src/supportPortal/services/services'

const ReceiptPrintingComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/receiptPrinting/ReceiptPrintingComponent')
)

const SORT_OPTIONS = [
  { label: 'Start Time', value: 'startTime' },
  { label: 'Booking Time', value: 'createdTime' },
]

function mergeEventTypeOptions(baseOptions = [], extraOptions = []) {
  const merged = []
  ;[...baseOptions, ...extraOptions].forEach((option) => {
    if (!option?.id || merged.some((existing) => existing.id === option.id)) {
      return
    }
    merged.push(option)
  })
  return merged
}

function getRecordingPolicySourceLabel(source) {
  switch (source) {
    case 'event':
      return 'Event override'
    case 'patient':
      return 'Patient default'
    case 'default-off':
      return 'Default off'
    default:
      return null
  }
}

function isBookedEventCompleted(bookedEvent) {
  return Boolean(
    bookedEvent?.markedCompleted ||
      bookedEvent?.sessionStatus === 'marked-completed' ||
      bookedEvent?.sessionStatus === 'finished',
  )
}

function getSessionMediaCapabilities(bookedEvent, hasVcAuditAccess, canManageRecordingPreferenceForRole = false) {
  const completed = isBookedEventCompleted(bookedEvent)
  const canManageRecordingPreference =
    !completed && canManageRecordingPreferenceForRole && bookedEvent?.canManageRecordingPreference === true
  const isRecordingEnabled = bookedEvent?.resolvedRecordingEnabled === true
  const sourceLabel = getRecordingPolicySourceLabel(bookedEvent?.resolvedRecordingPolicySource)
  const hasMediaAccess = hasVcAuditAccess || canManageRecordingPreference
  const canPlayLive = hasVcAuditAccess && bookedEvent?.vcAuditVisible && bookedEvent?.vcAuditCanViewLive === true
  const recordingCount = Number(bookedEvent?.vcAuditRecordingCount || 0)
  const canPlayRecording =
    hasVcAuditAccess &&
    bookedEvent?.vcAuditVisible &&
    bookedEvent?.vcAuditCanViewRecordings === true &&
    recordingCount > 0
  const latestRecordingId = bookedEvent?.vcAuditLatestRecordingId

  return {
    canManageRecordingPreference,
    canPlayLive,
    canPlayRecording,
    hasAnyMediaAction:
      hasMediaAccess &&
      (canManageRecordingPreference || canPlayLive || canPlayRecording),
    isRecordingEnabled,
    latestRecordingId,
    recordingCount,
    sourceLabel,
  }
}

function SessionMediaActionItems({
  bookedEvent,
  hasVcAuditAccess,
  canManageRecordingPreferenceForRole = false,
  onSuccess,
  BookingActionButton,
}) {
  const navigate = useNavigate()
  const [isUpdating, setIsUpdating] = useState(false)
  const {
    canManageRecordingPreference,
    canPlayLive,
    canPlayRecording,
    hasAnyMediaAction,
    isRecordingEnabled,
    latestRecordingId,
    recordingCount,
    sourceLabel,
  } = getSessionMediaCapabilities(bookedEvent, hasVcAuditAccess, canManageRecordingPreferenceForRole)

  if (!BookingActionButton || !hasAnyMediaAction) {
    return null
  }

  const handleToggle = async () => {
    if (!bookedEvent?.id || isUpdating || !canManageRecordingPreference) {
      return
    }

    setIsUpdating(true)
    const loadingToast = toast.loading('Updating recording preference...')
    const apiUrl = isRecordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-not-recordable'
      : '/api/secure/cc/support/events/mark-event-as-recordable'

    try {
      const response = await putSPDataWithParams(apiUrl, null, {
        'booked-event-id': bookedEvent.id,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        toast.update(loadingToast, {
          render: isRecordingEnabled
            ? 'Recording disabled for this event'
            : 'Recording enabled for this event',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        })
        onSuccess?.()
        return
      }

      toast.update(loadingToast, {
        render: response?.data?.message || 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } catch (error) {
      toast.update(loadingToast, {
        render: 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      {canManageRecordingPreference ? (
        <BookingActionButton
          label={isUpdating ? 'Updating...' : isRecordingEnabled ? 'Recording On' : 'Recording Off'}
          onClick={handleToggle}
          disabled={isUpdating}
          tone="default"
          startIcon={<FiberManualRecordIcon sx={{ fontSize: 15 }} />}
          title={
            sourceLabel
              ? `Recording policy source: ${sourceLabel}.`
              : 'Change the recording preference for this booked event.'
          }
          style={{
            color: isRecordingEnabled ? '#047857' : '#b91c1c',
            borderColor: isRecordingEnabled ? 'rgba(5, 150, 105, 0.34)' : 'rgba(220, 38, 38, 0.32)',
            backgroundColor: isRecordingEnabled ? 'rgba(209, 250, 229, 0.56)' : 'rgba(254, 226, 226, 0.62)',
          }}
        />
      ) : null}
      {canPlayLive ? (
        <BookingActionButton
          label={bookedEvent?.vcAuditLiveBadgeLabel || 'Live'}
          onClick={() =>
            navigate(`/support-home/booked-events/${bookedEvent?.id}/live`, {
              state: { from: '/support-home/booked-events' },
            })
          }
          tone="default"
          startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 17 }} />}
          title="Open live session media."
          style={{
            color: '#b91c1c',
            borderColor: 'rgba(220, 38, 38, 0.34)',
            backgroundColor: 'rgba(254, 226, 226, 0.62)',
          }}
        />
      ) : null}
      {canPlayRecording ? (
        <BookingActionButton
          label={recordingCount > 1 ? `Recordings (${recordingCount})` : 'Recording'}
          onClick={() => {
            const path = latestRecordingId
              ? `/support-home/booked-events/${bookedEvent?.id}/recordings/${latestRecordingId}`
              : `/support-home/booked-events/${bookedEvent?.id}/recordings`
            navigate(path, {
              state: { from: '/support-home/booked-events' },
            })
          }}
          tone="default"
          startIcon={<VideoLibraryOutlinedIcon sx={{ fontSize: 17 }} />}
          title={`Open ${recordingCount} available recording${recordingCount > 1 ? 's' : ''}.`}
        />
      ) : null}
    </>
  )
}

function LegacyBookedEventsView({
  showHeader = true,
  eventTypeOptions = [],
  hasVcAuditAccess = false,
  hasRecordingPreferenceAccess = false,
  hasInvoiceAccess = false,
  heading = 'Bookings & Appointments',
  helperText = '',
  defaultShowOnlyPaidEvents = true,
  defaultFilters = {},
  showPaymentPendingToggle = true,
}) {
  const [selectedEventType, setSelectedEventType] = useState(
    eventTypeOptions[0]?.id || null,
  )
  const [sortingHandler, setSortingHandler] = useState(null)
  const [showOnlyPaidEvents, setShowOnlyPaidEvents] = useState(defaultShowOnlyPaidEvents)
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [selectedEventForEngagement, setSelectedEventForEngagement] = useState(null)
  const [rescheduleDrawerBooking, setRescheduleDrawerBooking] = useState(null)
  const [invoiceDialogState, setInvoiceDialogState] = useState({
    open: false,
    bookedEvent: null,
    openSendDialogOnReady: false,
  })
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])
  const listingRef = useRef(null)

  useEffect(() => {
    if (
      eventTypeOptions.length > 0 &&
      !eventTypeOptions.some((option) => option.id === selectedEventType)
    ) {
      setSelectedEventType(eventTypeOptions[0].id)
    } else if (eventTypeOptions.length === 0 && selectedEventType !== null) {
      setSelectedEventType(null)
    }
  }, [eventTypeOptions, selectedEventType])

  const handleEngagementClick = (bookedEvent) => {
    setSelectedEventForEngagement(bookedEvent)
    setEngagementDialogOpen(true)
  }

  const closeInvoiceDialog = useCallback(() => {
    setInvoiceDialogState({
      open: false,
      bookedEvent: null,
      openSendDialogOnReady: false,
    })
  }, [])

  const openInvoiceDialog = useCallback((bookedEvent, { openSendDialogOnReady = false } = {}) => {
    if (!bookedEvent?.id) {
      return
    }

    setInvoiceDialogState({
      open: true,
      bookedEvent,
      openSendDialogOnReady,
    })
  }, [])

  const receiptCurrentClinic = useMemo(() => {
    const eventData = invoiceDialogState.bookedEvent
    if (!eventData) return null
    const title =
      eventData?.clinicLocation?.title ||
      eventData?.clinicLocation?.name ||
      eventData?.clinicName ||
      eventData?.clinicLocationName ||
      eventData?.clinicLocationTitle
    const address =
      eventData?.clinicLocation?.address ||
      eventData?.address ||
      eventData?.clinicAddress ||
      eventData?.clinicLocationAddress
    const mobile =
      eventData?.clinicLocation?.mobile ||
      eventData?.mobile
    const clinicBrandName =
      eventData?.clinicBrandName ||
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title
    const clinicBrandShortName = eventData?.clinicBrandData?.shortName
    const brandName =
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title

    return {
      clinicBrandName,
      clinicBrandShortName,
      brandName,
      title,
      clinicLocationName: title,
      location: address != null ? { address } : null,
      address,
      mobile,
    }
  }, [invoiceDialogState.bookedEvent])

  return (
    <>
      {showHeader ? <SupportHeader heading={heading} /> : null}
      {helperText ? (
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%', px: 1.5, pt: 1.5 }}>
          <Alert severity="info">{helperText}</Alert>
        </Box>
      ) : null}
      <div
        style={{
          background: '#35304a',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          gap: '12px',
        }}
      >
        <div>
          {eventTypeOptions.length > 0 ? (
            <BaseUITabsComponent
              value={selectedEventType}
              onChange={setSelectedEventType}
              tabs={eventTypeOptions}
              getTabLabel={(tab) => tab?.title}
              getTabId={(tab) => tab?.id}
            />
          ) : null}
        </div>
        <div
          className="d-flex align-items-center justify-content-end gap-2"
          style={{ color: 'white !important' }}
        >
          {showPaymentPendingToggle ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyPaidEvents}
                  onChange={() => setShowOnlyPaidEvents(!showOnlyPaidEvents)}
                  sx={{
                    color: 'white !important',
                    '&.Mui-checked': {
                      color: 'white',
                    },
                  }}
                />
              }
              label="Hide payment pending events"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: 'white !important',
                },
              }}
            />
          ) : null}
          <SortingHandler
            sortValue={sortingHandler}
            setSortValue={setSortingHandler}
            getOptionLabel={(option) => option?.label}
            getOptionValue={(option) => option?.value}
            sortOptions={SORT_OPTIONS}
            outerDivStyle={{
              marginLeft: '10px',
              color: 'white',
              fontSize: '14px',
              alignItems: 'center',
            }}
            buttonColor="primary"
            buttonVariant="contained"
          />
        </div>
      </div>
      <div style={{ maxWidth: '1360px', margin: '0 auto', width: '100%', padding: '0 12px', boxSizing: 'border-box' }}>
        <MentorPastEventsListing
          ref={listingRef}
          key={`${selectedEventType || 'all'}-${showOnlyPaidEvents}`}
          listingApiSignature="/api/secure/cc/support/events/all/paginated-unified"
          showMentorName
          hidePortalVoiceCallAction
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
          skipCancelCheckInGuard
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          eventType={selectedEventType || undefined}
          sortingHandler={sortingHandler}
          showOnlyPaidEvents={showOnlyPaidEvents}
          defaultFilters={defaultFilters}
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          filterByBookedStatus={null}
          eventTypeOptions={eventTypeOptions}
          onRescheduleBooking={(bookedEvent) => setRescheduleDrawerBooking(bookedEvent)}
          onEngagementClick={hasEngagementPermission ? handleEngagementClick : undefined}
          bookingCardLayout="support"
          renderActionsHeaderTrailing={(bookedEvent) => (
            <SupportContactActions
              patientId={bookedEvent?.patientId}
              practitionerId={bookedEvent?.mentorId}
              bookedEventId={bookedEvent?.id}
              clinicBrandId={bookedEvent?.clinicBrandId}
              clinicId={bookedEvent?.clinicId}
              targetRole="patient"
              eventType={bookedEvent?.eventType || selectedEventType}
              title=""
              compactIconStrip
              uiSurface="support-booked-events-strip"
              sx={{
                px: 0.45,
                py: 0.3,
                borderRadius: 999,
                border: '1px solid rgba(96, 165, 250, 0.22)',
                background:
                  'linear-gradient(135deg, rgba(239, 246, 255, 0.94) 0%, rgba(240, 253, 250, 0.9) 100%)',
              }}
            />
          )}
          renderManageSessionActionItems={(bookedEvent, { BookingActionButton }) => {
            const sessionMediaCapabilities = getSessionMediaCapabilities(
              bookedEvent,
              hasVcAuditAccess,
              hasRecordingPreferenceAccess,
            )

            if (!sessionMediaCapabilities.hasAnyMediaAction) {
              return null
            }

            return (
              <SessionMediaActionItems
                key="session-media-actions"
                bookedEvent={bookedEvent}
                hasVcAuditAccess={hasVcAuditAccess}
                canManageRecordingPreferenceForRole={hasRecordingPreferenceAccess}
                onSuccess={() => listingRef.current?.resetData?.()}
                BookingActionButton={BookingActionButton}
              />
            )
          }}
          renderViewActionItems={(bookedEvent, { BookingActionButton }) => {
            if (!hasInvoiceAccess || !bookedEvent?.id) {
              return null
            }

            return [
              <BookingActionButton
                key="generate-invoice"
                label="Generate Invoice"
                onClick={() => openInvoiceDialog(bookedEvent)}
                tone="payment"
                startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 17 }} />}
              />,
              <BookingActionButton
                key="send-invoice"
                label="Send Invoice"
                onClick={() => openInvoiceDialog(bookedEvent, { openSendDialogOnReady: true })}
                tone="payment"
                startIcon={<SendOutlinedIcon sx={{ fontSize: 17 }} />}
              />,
            ]
          }}
        />
      </div>

      {hasEngagementPermission && (
        <EngagementSettingsDialog
          open={engagementDialogOpen}
          onClose={() => {
            setEngagementDialogOpen(false)
            setSelectedEventForEngagement(null)
          }}
          targetType="booked-event"
          target={selectedEventForEngagement}
          onSuccess={() => {}}
        />
      )}

      <SupportBookedEventRescheduleDrawer
        open={Boolean(rescheduleDrawerBooking)}
        bookedEvent={rescheduleDrawerBooking}
        onClose={() => setRescheduleDrawerBooking(null)}
        onSuccess={() => {
          setRescheduleDrawerBooking(null)
          listingRef.current?.resetData?.()
        }}
      />

      <Dialog
        open={invoiceDialogState.open}
        PaperComponent={ReceiptPrintDialogPaper}
        onClose={closeInvoiceDialog}
        className="receipt-print-dialog"
      >
        <Suspense
          fallback={
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          }
        >
          <ReceiptPrintingComponent
            bookedEvent={invoiceDialogState.bookedEvent}
            bookedEventId={invoiceDialogState.bookedEvent?.id}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
            closeDialog={closeInvoiceDialog}
            fetchDataWithoutParams={getSPDataWithoutParams}
            currentClinic={receiptCurrentClinic}
            sendReceiptApiSignature="/api/secure/cc/support/receipt/send"
            postDataForSendReceipt={postSPDataWithoutParams}
            documentKindLabel="Invoice"
            openSendDialogOnReady={invoiceDialogState.openSendDialogOnReady}
          />
        </Suspense>
      </Dialog>
    </>
  )
}

export default function BookedEvents({ pageMode = 'default' }) {
  const userRoles = getUserRoles()
  const hasVcAuditAccess = canViewSupportVcAuditBookings(userRoles)
  const hasSupportBookingsAccess = canViewSupportBookings(userRoles)
  const hasRecordingPreferenceAccess = canManageSupportRecordingPreferences(userRoles)
  const hasInvoiceAccess = canManageSupportBookedEventInvoices(userRoles)
  const allowedSupportEventTypes = useMemo(() => {
    const baseOptions = getAllowedSupportBookedEventTypeOptions(userRoles)
    if (!hasVcAuditAccess) {
      return baseOptions
    }
    return mergeEventTypeOptions(baseOptions, SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS)
  }, [hasVcAuditAccess, userRoles])
  const pageConfig = useMemo(() => {
    if (pageMode === 'payment-links') {
      return {
        heading: 'Payment Links',
        helperText: 'Review payment-pending bookings and open their payment links from one place.',
        defaultShowOnlyPaidEvents: false,
        defaultFilters: {
          paymentStatusIds: [BOOKING_PAYMENT_STATUSES.PENDING],
        },
        showPaymentPendingToggle: false,
      }
    }

    return {
      heading: 'Bookings & Appointments',
      helperText: '',
      defaultShowOnlyPaidEvents: true,
      defaultFilters: {},
      showPaymentPendingToggle: true,
    }
  }, [pageMode])

  if (!hasVcAuditAccess && !hasSupportBookingsAccess) {
    return <Navigate to="/support-home" replace />
  }

  return (
    <LegacyBookedEventsView
      showHeader
      eventTypeOptions={allowedSupportEventTypes}
      hasVcAuditAccess={hasVcAuditAccess}
      hasRecordingPreferenceAccess={hasRecordingPreferenceAccess}
      hasInvoiceAccess={hasInvoiceAccess}
      heading={pageConfig.heading}
      helperText={pageConfig.helperText}
      defaultShowOnlyPaidEvents={pageConfig.defaultShowOnlyPaidEvents}
      defaultFilters={pageConfig.defaultFilters}
      showPaymentPendingToggle={pageConfig.showPaymentPendingToggle}
    />
  )
}
